import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { ValidatorsEx } from '../../../../validators/validators-ex';
import { BaseComponent } from '../../../base/base.component';
import { InputComponent } from '../../../core/controls/input.component';
import { FieldContainerComponent } from '../../../core/fields/field-container.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { FilterSection, ICriteria } from '../edit-filter.component';

@Component({
  selector: 'app-lag-time-criteria',
  imports: [
    CommonModule,
    InputComponent,
    SelectFieldComponent,
    FieldContainerComponent,
  ],
  template: `
    <div class="tw-pt-2">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <app-select-field
          [control]="fg.controls.fromField"
          optionLabel="description"
          optionValue="value"
          [chip]="true"
          [options]="fromFields"
          [maxSelectedLabels]="10"
          [showHelpText]="false"
          label="From"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
        />

        <app-select-field
          [control]="fg.controls.toField"
          optionLabel="description"
          optionValue="value"
          [chip]="true"
          [options]="toFields"
          [maxSelectedLabels]="10"
          [showHelpText]="false"
          label="To"
          [disabled]="!fg.controls.fromField.value"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
        />

        <app-select-field
          [control]="fg.controls.operator"
          optionLabel="description"
          optionValue="value"
          [chip]="true"
          [options]="operators"
          [maxSelectedLabels]="10"
          [showHelpText]="false"
          label="Operator"
          [disabled]="
            !fg.controls.fromField.value || !fg.controls.toField.value
          "
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
        />

        <app-field-container
          label="Lag days"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
        >
          <ng-template #value>
            <div class="tw-flex tw-flex-col md:tw-flex-row tw-w-full tw-gap-2">
              <app-input
                type="Integer"
                [control]="fg.controls.fromDays"
                [showHelpText]="true"
                label="From"
                [min]="0"
                [max]="999"
              ></app-input>

              <app-input
                type="Integer"
                [control]="fg.controls.toDays"
                [showHelpText]="true"
                label="To"
                [min]="0"
                [max]="999"
              ></app-input>
            </div>
          </ng-template>
        </app-field-container>
      </div>
    </div>
  `,
  styles: ``,
})
export class LagTimeCriteriaComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, ICriteria
{
  @Input({ required: true }) section: FilterSection;

  showEmpty = true;

  fromFields: ListItem[] = [
    { value: '1', description: 'Date of loss' },
    { value: '2', description: 'Date claim reported by client' },
    { value: '3', description: 'Date claim reported to client' },
    { value: '4', description: 'Date claim reported to Sedgwick' },
    { value: '5', description: 'Date claim created' },
    { value: '6', description: 'Date claim opened' },
    { value: '7', description: 'Date claim closed' },
    { value: '8', description: 'Date claim reopened' },
  ];

  toFields: ListItem[] = [];

  operators: ListItem[] = [
    { value: 'BETWEEN', description: 'Between' },
    { value: 'EQUALS', description: 'Equal' },
    { value: 'GREATERTHAN', description: 'Greater than' },
    { value: 'GREATERTHANEQUALTO', description: 'Greater than equal to' },
    { value: 'LESSTHAN', description: 'Less than' },
    { value: 'LESSTHANEQUALTO', description: 'Less than equal to' },
    { value: 'NOTEQUALTO', description: 'Not equal to' },
  ];

  private readonly erfMapping: Record<string, string> = {
    '1|2': 'LAG_IN_REPORTING_DAYS',
    '1|3': 'LAG_IN_LOSS_AND_REP_BY_CLNT',
    '1|4': 'LAG_IN_CLIENT_REPORTING_DAYS',
    '1|5': 'LAG_IN_LOSS_AND_CREATED',
    '1|6': 'LAG_IN_LOSS_AND_OPENED',
    '1|7': 'LAG_IN_LOSS_AND_CLOSED',
    '1|8': 'LAG_IN_LOSS_AND_REOPENED',
    '2|1': 'LAG_IN_REP_TO_CLNT_AND_LOSS',
    '2|4': 'LAG_IN_REP_TO_CLNT_AND_REP_TO_CMS',
    '2|5': 'LAG_IN_REP_TO_CLNT_AND_CREATED',
    '2|6': 'LAG_IN_REP_TO_CLNT_AND_OPENED',
    '2|7': 'LAG_IN_REP_TO_CLNT_AND_CLOSED',
    '2|8': 'LAG_IN_REP_TO_CLNT_AND_REOPENED',
    '3|1': 'LAG_IN_REP_BY_CLNT_AND_LOSS',
    '3|2': 'LAG_IN_REP_TO_CLNT_AND_REP_BY_CLNT',
    '3|4': 'LAG_IN_REP_BY_CLNT_AND_REP_TO_CMS',
    '3|5': 'LAG_IN_REP_BY_CLNT_AND_CREATED',
    '3|6': 'LAG_IN_REP_BY_CLNT_AND_OPENED',
    '3|7': 'LAG_IN_REP_BY_CLNT_AND_CLOSED',
    '3|8': 'LAG_IN_REP_BY_CLNT_AND_REOPENED',
    '4|1': 'LAG_IN_REP_TO_CMS_AND_LOSS',
    '4|5': 'LAG_IN_REP_TO_CMS_AND_CREATED',
    '4|6': 'LAG_IN_REP_TO_CMS_AND_OPENED',
    '4|7': 'LAG_IN_REP_TO_CMS_AND_CLOSED',
    '4|8': 'LAG_IN_REP_TO_CMS_AND_REOPENED',
    '5|6': 'LAG_IN_CREATED_AND_OPENED',
    '5|7': 'LAG_IN_CREATED_AND_CLOSED',
    '5|8': 'LAG_IN_CREATED_AND_REOPENED',
    '6|7': 'LAG_IN_OPENED_AND_CLOSED',
    '6|8': 'LAG_IN_OPENED_AND_REOPENED',
    '7|8': 'LAG_IN_CLOSED_AND_REOPENED',
    '8|7': 'LAG_IN_REOPENED_AND_CLOSED',
  };

  fg = new FormGroup({
    fromField: new FormControl<string>(null),
    toField: new FormControl<string>(null),
    operator: new FormControl<string>(null),
    fromDays: new FormControl<number>(null, [Validators.required]),
    toDays: new FormControl<number>(null),
  });

  setLagTimeFromCriteria(criteria: FilterCriteria[]) {
    criteria.forEach((lagTime) => {
      const key = Object.entries(this.erfMapping).find(
        ([k, v]) => v === lagTime.column,
      )?.[0];

      if (!key) return;

      const [fromField, toField] = key?.split('|') || [];
      this.fg.controls.fromField.setValue(fromField);
      this.toFields = this.getToFieldsFor(fromField);
      this.fg.controls.toField.setValue(toField);
      this.fg.controls.operator.setValue(lagTime.operator);

      if (lagTime.operator === 'BETWEEN') {
        const [fromStr, toStr] = lagTime.values?.[0].split('|') || [];
        this.fg.controls.fromDays.setValue(Number(fromStr));
        this.fg.controls.toDays.setValue(Number(toStr));
      } else {
        this.fg.controls.fromDays.setValue(Number(lagTime.values?.[0]));
      }
    });
  }

  private getToFieldsFor(fromValue: string): ListItem[] {
    switch (fromValue) {
      case '1':
        return this.fromFields.filter((f) =>
          ['2', '3', '4', '5', '6', '7', '8'].includes(f.value),
        );
      case '2':
        return this.fromFields.filter((f) =>
          ['1', '4', '5', '6', '7', '8'].includes(f.value),
        );
      case '3':
        return this.fromFields.filter((f) =>
          ['1', '2', '4', '5', '6', '7', '8'].includes(f.value),
        );
      case '4':
        return this.fromFields.filter((f) =>
          ['1', '5', '6', '7', '8'].includes(f.value),
        );
      case '5':
        return this.fromFields.filter((f) => ['6', '7', '8'].includes(f.value));
      case '6':
        return this.fromFields.filter((f) => ['7', '8'].includes(f.value));
      case '7':
        return this.fromFields.filter((f) => ['8'].includes(f.value));
      case '8':
        return this.fromFields.filter((f) => ['7'].includes(f.value));
      default:
        return [];
    }
  }

  async ngOnInit() {
    this.section.criteria = this;

    this.fg.controls.fromDays.disable();
    this.fg.controls.toDays.disable();

    this.dispose.subs = this.fg.controls.fromField.valueChanges.subscribe(
      (value) => {
        this.fg.controls.toField.setValue(null);
        this.fg.controls.operator.setValue(null);
        this.fg.controls.fromDays.setValue(null);
        this.fg.controls.toDays.setValue(null);
        this.toFields = this.getToFieldsFor(value);
        this.emitCount();
      },
    );

    this.dispose.subs = this.fg.controls.toField.valueChanges.subscribe(
      (value) => {
        if (value) {
          this.fg.controls.operator.enable();
        } else {
          this.fg.controls.operator.disable();
          this.fg.controls.operator.setValue(null);
          this.fg.controls.fromDays.setValue(null);
          this.fg.controls.toDays.setValue(null);
        }
        this.emitCount();
      },
    );

    this.dispose.subs = this.fg.controls.operator.valueChanges.subscribe(
      (value) => {
        if (value) {
          this.fg.controls.fromDays.enable();
        } else {
          this.fg.controls.fromDays.disable();
          this.fg.controls.fromDays.setValue(null);
        }

        if (value === 'BETWEEN') {
          this.fg.controls.toDays.enable();
          this.fg.controls.fromDays.addValidators([
            ValidatorsEx.integerLessThan(
              this.fg.controls.toDays,
              false,
              'From value must be less than To value',
            ),
          ]);
          this.fg.controls.toDays.addValidators([
            Validators.required,
            ValidatorsEx.integerGreaterThan(
              this.fg.controls.fromDays,
              false,
              'To value must be greater than From value',
            ),
          ]);
        } else {
          this.fg.controls.toDays.disable();
          this.fg.controls.toDays.clearValidators();
          this.fg.controls.toDays.setValue(null);
        }

        this.fg.controls.fromDays.updateValueAndValidity();
        this.fg.controls.toDays.updateValueAndValidity();
        this.emitCount();
      },
    );

    this.dispose.subs = this.fg.controls.fromDays.valueChanges.subscribe(() => {
      this.fg.controls.toDays.updateValueAndValidity({ emitEvent: false });
      this.emitCount();
    });

    this.dispose.subs = this.fg.controls.toDays.valueChanges.subscribe(() => {
      this.fg.controls.fromDays.updateValueAndValidity({ emitEvent: false });
      this.emitCount();
    });
  }

  ngAfterViewInit() {
    if (this.section.values?.length) {
      this.setLagTimeFromCriteria(this.section.values);
    }
  }

  valid(): boolean {
    return this.fg.valid;
  }

  getCriteria(): FilterCriteria[] {
    const fromField = this.fg.controls.fromField.value;
    const toField = this.fg.controls.toField.value;
    const operator = this.fg.controls.operator.value;
    const fromDays = this.fg.controls.fromDays.value;
    const toDays = this.fg.controls.toDays.value;

    if (!fromField || !toField || !operator || fromDays === null) {
      return [];
    }

    const fieldName = this.erfMapping[`${fromField}|${toField}`];
    let values: string[];

    if (operator === 'BETWEEN' && toDays !== null) {
      values = [`${fromDays}|${toDays}`];
    } else {
      values = [`${fromDays}`];
    }

    return [
      {
        tableAlias: 'claim',
        column: fieldName,
        displayName: '',
        operator: operator,
        values: values,
        lineOfBusiness: '',
        dataType: 'NUMBER',
        isClaimLab: false,
        formattedOperator: '',
        formattedValue: '',
        filterSection: 'Lag Time',
        filterSubsection: '',
      },
    ];
  }

  private emitCount(): void {
    const hasFromField = !!this.fg.controls.fromField.value;
    const hasToField = !!this.fg.controls.toField.value;
    const operator = this.fg.controls.operator.value;
    const hasFromDays = this.fg.controls.fromDays.value !== null;
    const hasToDays = this.fg.controls.toDays.value !== null;

    let isComplete = hasFromField && hasToField && !!operator && hasFromDays;

    if (operator === 'BETWEEN') {
      isComplete = isComplete && hasToDays;
    }

    this.section.count.set(isComplete ? 1 : 0);
  }
}
